import { useSeo } from '../lib/seo';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, MapIcon, Grid3x3, X, Users2, Gift, Dog, CloudRain, Landmark, UtensilsCrossed, CalendarDays } from 'lucide-react';
import api from '../lib/api';
import { track } from '../lib/tracker';
import AttractionCard from '../components/AttractionCard';
import EventCard from '../components/EventCard';
import CountyMap from '../components/CountyMap';
import { AccessibilityDisclaimer } from '../components/Accessibility';

const CATEGORIES = [
  { value: '', label: 'Everything' },
  { value: 'days_out', label: 'Days Out' },
  { value: 'heritage', label: 'Heritage' },
  { value: 'outdoors', label: 'Outdoors' },
  { value: 'food_drink', label: 'Places to Eat and Drink' },
  { value: 'events', label: "What's On" },
];

const FLAGS = [
  { key: 'family', label: 'Family' },
  { key: 'dog', label: 'Dog friendly' },
  { key: 'free', label: 'Free entry' },
  { key: 'indoor', label: 'Indoor' },
  { key: 'outdoor', label: 'Outdoor' },
];

// Accessibility filters — separate group so they're scannable and so the
// disclaimer can be surfaced whenever any of them is active. Each value maps
// 1:1 to the backend `/attractions` query param added in Phase 4.
const A11Y_FILTERS = [
  // Step-free entrance filter suspended pending better data coverage — see /admin.
  // { key: 'step_free', label: 'Step-free entrance' },
  { key: 'changing_places', label: 'Changing Places toilet' },
  { key: 'hearing_loop', label: 'Hearing loop' },
  { key: 'sensory_friendly', label: 'Sensory-friendly sessions' },
  { key: 'assistance_dogs', label: 'Assistance dogs welcome' },
];

// Themed discovery routes — one-tap presets that set the underlying filters.
const THEMES = [
  { label: 'Family days out', icon: Users2, params: { family: 'true' } },
  { label: 'Free things to do', icon: Gift, params: { free: 'true' } },
  { label: 'Dog friendly', icon: Dog, params: { dog: 'true' } },
  { label: 'Rainy-day ideas', icon: CloudRain, params: { indoor: 'true' } },
  { label: 'Heritage & history', icon: Landmark, params: { category: 'heritage' } },
  { label: 'Eat & drink', icon: UtensilsCrossed, params: { category: 'food_drink' } },
  { label: "What's on", icon: CalendarDays, params: { category: 'events' } },
];

export default function ExplorePage() {
  useSeo(
    'Places to Visit in Leicester & Leicestershire — Venues & Attractions | GoLeicestershire',
    'Places to visit across Leicester and Leicestershire: search and filter venues and attractions by town, type and accessibility — castles, country parks, museums, family days out and more.'
  );
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [townOptions, setTownOptions] = useState([]);
  const [facets, setFacets] = useState(null);
  const [globalFacets, setGlobalFacets] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventTownCounts, setEventTownCounts] = useState({});

  const currentCategory = params.get('category') || '';
  const currentTown = params.get('town') || '';
  const activeFlags = useMemo(() => FLAGS.filter((f) => params.get(f.key) === 'true').map((f) => f.key), [params]);
  const activeA11y = useMemo(() => A11Y_FILTERS.filter((f) => params.get(f.key) === 'true').map((f) => f.key), [params]);
  // Events join the explore results on "Everything" and "What's On" — venue
  // feature flags (dog friendly, indoor…) don't apply to events, so any active
  // flag hides them rather than silently ignoring the filter.
  const eventsMode = currentCategory === 'events';
  const showEvents = (eventsMode || currentCategory === '') && activeFlags.length === 0 && activeA11y.length === 0;

  useEffect(() => {
    api.get('/attractions/towns/list').then((r) => setTownOptions(r.data?.items || [])).catch(() => {});
    api.get('/attractions/facets').then((r) => setGlobalFacets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (eventsMode) {
      setData({ items: [], total: 0 });
      setFacets(null);
      return;
    }
    const q = new URLSearchParams();
    if (params.get('category')) q.set('category', params.get('category'));
    if (params.get('town')) q.set('town', params.get('town'));
    if (search) q.set('search', search);
    FLAGS.forEach((f) => { if (params.get(f.key) === 'true') q.set(f.key, 'true'); });
    A11Y_FILTERS.forEach((f) => { if (params.get(f.key) === 'true') q.set(f.key, 'true'); });
    api.get(`/attractions/facets?${q.toString()}`).then((r) => setFacets(r.data)).catch(() => {});
    q.set('limit', '120');
    api.get(`/attractions?${q.toString()}`).then((r) => setData(r.data));
  }, [params, search, eventsMode]);

  useEffect(() => {
    if (!showEvents) { setEvents([]); return; }
    const q = new URLSearchParams({ upcoming: 'true', limit: '120' });
    if (currentTown) q.set('town', currentTown);
    if (search) q.set('search', search);
    api.get(`/events?${q.toString()}`).then((r) => {
      const items = r.data?.items || [];
      setEvents(items);
      if (!currentTown && !search) {
        const m = {};
        items.forEach((e) => { const t = (e.town || '').trim(); if (t) m[t] = (m[t] || 0) + 1; });
        setEventTownCounts(m);
      }
    }).catch(() => setEvents([]));
  }, [showEvents, currentTown, search]);

  // Track searches — debounced 800ms so we don't spam on every keystroke
  useEffect(() => {
    if (!search || search.length < 2) return;
    const t = setTimeout(() => {
      const filters = {};
      FLAGS.forEach((f) => { if (params.get(f.key) === 'true') filters[f.key] = true; });
      if (params.get('category')) filters.category = params.get('category');
      if (params.get('town')) filters.town = params.get('town');
      track.search(search, filters);
    }, 800);
    return () => clearTimeout(t);
  }, [search, params]);

  function toggle(key, value = 'true') {
    const np = new URLSearchParams(params);
    if (np.get(key) === value) np.delete(key);
    else np.set(key, value);
    setParams(np);
  }
  function setCategory(c) {
    const np = new URLSearchParams(params);
    if (c) np.set('category', c); else np.delete('category');
    if (c === 'events') {
      // Venue feature filters don't apply to events — clear them so the
      // visitor doesn't end up with silently-ignored filters.
      FLAGS.forEach((f) => np.delete(f.key));
      A11Y_FILTERS.forEach((f) => np.delete(f.key));
    }
    setParams(np);
  }
  function setTown(t) {
    const np = new URLSearchParams(params);
    if (t) np.set('town', t); else np.delete('town');
    setParams(np);
  }
  function clearAll() {
    setParams(new URLSearchParams());
    setSearch('');
  }
  function applyTheme(t) {
    setParams(new URLSearchParams(t.params));
    setSearch('');
  }
  const themeActive = (t) => Object.entries(t.params).every(([k, v]) => params.get(k) === v);

  // Dynamic facets — only offer filter options that return results given the
  // other active filters, so visitors can never click into zero results.
  const catCount = (v) => (facets ? (facets.categories?.[v] ?? 0) : 1);
  const flagCount = (k) => (facets ? (facets.flags?.[k] ?? 0) : 1);
  const a11yCount = (k) => (facets ? (facets.a11y?.[k] ?? 0) : 1);
  const visibleCategories = CATEGORIES.filter((c) => !c.value || c.value === 'events' || c.value === currentCategory || catCount(c.value) > 0);
  const visibleFlags = FLAGS.filter((f) => activeFlags.includes(f.key) || flagCount(f.key) > 0);
  const visibleA11y = A11Y_FILTERS.filter((f) => activeA11y.includes(f.key) || a11yCount(f.key) > 0);
  const facetTowns = eventsMode && Object.keys(eventTownCounts).length
    ? Object.keys(eventTownCounts).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    : facets
      ? Object.entries(facets.towns).filter(([t, n]) => n > 0 || t === currentTown).map(([t]) => t)
      : townOptions;
  // Chips: biggest towns first (alphabetical order was hiding Loughborough et al past the cut)
  const chipTowns = eventsMode && Object.keys(eventTownCounts).length
    ? Object.entries(eventTownCounts).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([t]) => t)
    : facets
      ? Object.entries(facets.towns).filter(([t, n]) => n > 0 || t === currentTown)
          .sort((a, b) => b[1] - a[1]).slice(0, 12).map(([t]) => t)
      : townOptions.slice(0, 12);
  const visibleThemes = THEMES.filter((t) => {
    if (t.params.category === 'events') return true;
    if (!globalFacets) return true;
    if (t.params.category) return (globalFacets.categories?.[t.params.category] ?? 0) > 0;
    return (globalFacets.flags?.[Object.keys(t.params)[0]] ?? 0) > 0;
  });
  const combinedTotal = (eventsMode ? 0 : data.total) + (showEvents ? events.length : 0);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
      <div className="mb-12 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="label text-heritage mb-3">Explore Leicestershire</div>
          <h1 className="font-display text-5xl lg:text-7xl leading-[0.95]">{combinedTotal} {combinedTotal === 1 ? 'way' : 'ways'} to spend a day.</h1>
        </div>
        {!eventsMode && (
        <div className="flex items-center gap-2 bg-white border border-charcoal/10 rounded-full p-1.5">
          <button onClick={() => setView('grid')} data-testid="view-grid" className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${view === 'grid' ? 'bg-charcoal text-base' : 'text-charcoal/70'}`}>
            <Grid3x3 size={14}/> Grid
          </button>
          <button onClick={() => setView('map')} data-testid="view-map" className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${view === 'map' ? 'bg-charcoal text-base' : 'text-charcoal/70'}`}>
            <MapIcon size={14}/> Map
          </button>
        </div>
        )}
      </div>

      {/* Themed discovery routes — one-tap ways into the content */}
      <div className="mb-8 -mx-6 px-6 overflow-x-auto scrollbar-hide" data-testid="explore-themes">
        <div className="flex gap-3 w-max">
          {visibleThemes.map((t) => {
            const active = themeActive(t);
            const Icon = t.icon;
            return (
              <button key={t.label} onClick={() => applyTheme(t)}
                data-testid={`theme-${t.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className={`group flex items-center gap-2.5 rounded-2xl border px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition-all ${
                  active ? 'bg-forest text-base border-forest' : 'bg-white border-charcoal/10 hover:border-forest hover:-translate-y-0.5 hover:shadow-soft'
                }`}>
                <Icon size={17} className={active ? 'text-mustard' : 'text-heritage'} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-10 space-y-4">
        <div className="flex flex-wrap gap-2">
          {visibleCategories.map((c) => (
            <button
              key={c.value}
              onClick={() => setCategory(c.value)}
              data-testid={`filter-category-${c.value || 'all'}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                currentCategory === c.value
                  ? 'bg-forest text-base border border-forest'
                  : 'bg-white border border-charcoal/15 hover:border-forest hover:text-forest'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        {!eventsMode && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="label text-charcoal/50 mr-2">Filters:</span>
          {visibleFlags.map((f) => (
            <button
              key={f.key}
              onClick={() => toggle(f.key)}
              data-testid={`filter-flag-${f.key}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                activeFlags.includes(f.key)
                  ? 'bg-heritage text-base'
                  : 'bg-white border border-charcoal/15 hover:border-heritage'
              }`}
            >
              {f.label}
            </button>
          ))}
          <select
            value={currentTown}
            onChange={(e) => setTown(e.target.value)}
            data-testid="filter-town"
            className="ml-auto bg-white border border-charcoal/15 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-forest"
          >
            <option value="">Anywhere</option>
            {facetTowns.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="bg-white border border-charcoal/15 rounded-full px-5 py-2 text-sm w-56 focus:outline-none focus:border-forest"
            data-testid="search-input"
          />
          {(activeFlags.length > 0 || activeA11y.length > 0 || currentCategory || currentTown || search) && (
            <button onClick={clearAll} data-testid="filters-clear" className="text-sm text-muted hover:text-heritage inline-flex items-center gap-1">
              <X size={14}/> Clear
            </button>
          )}
        </div>
        )}
        {eventsMode && (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={currentTown}
              onChange={(e) => setTown(e.target.value)}
              data-testid="filter-town"
              className="bg-white border border-charcoal/15 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-forest"
            >
              <option value="">Anywhere</option>
              {facetTowns.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events..."
              className="bg-white border border-charcoal/15 rounded-full px-5 py-2 text-sm w-56 focus:outline-none focus:border-forest"
              data-testid="search-input-events"
            />
            {(currentCategory || currentTown || search) && (
              <button onClick={clearAll} data-testid="filters-clear-events" className="text-sm text-muted hover:text-heritage inline-flex items-center gap-1">
                <X size={14}/> Clear
              </button>
            )}
          </div>
        )}

        {/* Geographic discovery — browse by town */}
        {chipTowns.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="explore-towns">
            <span className="label text-charcoal/50 mr-2">Browse by town:</span>
            {chipTowns.map((t) => (
              <button key={t} onClick={() => setTown(currentTown === t ? '' : t)}
                data-testid={`town-chip-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                  currentTown === t ? 'bg-charcoal text-base' : 'bg-white border border-charcoal/15 hover:border-charcoal'
                }`}>
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Accessibility filter row — kept visually distinct so it's scannable
            for visitors with access needs. When any filter here is on, the
            inline disclaimer renders above the results to set expectations
            about data sources + how to report problems. */}
        {!eventsMode && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="label text-charcoal/50 mr-2 inline-flex items-center gap-1.5"><Filter size={11}/> Access needs:</span>
          {visibleA11y.map((f) => (
            <button
              key={f.key}
              onClick={() => toggle(f.key)}
              data-testid={`filter-a11y-${f.key}`}
              aria-pressed={activeA11y.includes(f.key)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                activeA11y.includes(f.key)
                  ? 'bg-forest text-base'
                  : 'bg-white border border-charcoal/15 hover:border-forest hover:text-forest'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        )}
      </div>

      {activeA11y.length > 0 && (
        <div className="mb-8">
          <AccessibilityDisclaimer variant="inline" />
        </div>
      )}

      {(currentCategory || currentTown || search || activeFlags.length > 0 || activeA11y.length > 0) && combinedTotal < 3 && (
        <div className="mb-8 bg-white border border-mustard/60 rounded-2xl px-6 py-5 flex flex-wrap items-center justify-between gap-4" data-testid="few-results-notice">
          <p className="text-sm text-charcoal/80">
            {combinedTotal === 0
              ? "We couldn't find anything matching all of those filters."
              : `Only ${combinedTotal} ${combinedTotal === 1 ? 'place matches' : 'places match'} all of those filters.`}{' '}
            Try relaxing one or two criteria — there's plenty more to discover nearby.
          </p>
          <button onClick={clearAll} data-testid="few-results-clear"
            className="inline-flex items-center gap-1.5 bg-forest text-base px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-forest/90 transition-colors shrink-0">
            <X size={14}/> Clear all filters
          </button>
        </div>
      )}

      {view === 'map' && !eventsMode ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <CountyMap items={data.items} height={680} />
          </div>
          <div className="lg:col-span-4 max-h-[680px] overflow-y-auto pr-2 scrollbar-hide space-y-4">
            {data.items.slice(0, 30).map((a) => <AttractionCard key={a.id || a.slug} a={a} />)}
          </div>
        </div>
      ) : (
        <>
          {!eventsMode && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {data.items.map((a) => <AttractionCard key={a.id || a.slug} a={a} />)}
            </div>
          )}
          {showEvents && events.length > 0 && (
            <div className={eventsMode ? '' : 'mt-16'} data-testid="explore-events-section">
              {!eventsMode && (
                <div className="mb-8">
                  <div className="label text-heritage mb-2 inline-flex items-center gap-2"><CalendarDays size={12}/> What's on</div>
                  <h2 className="font-display text-3xl md:text-4xl">Upcoming events{currentTown ? ` in ${currentTown}` : ''}</h2>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {events.map((ev) => <EventCard key={ev.id || ev.slug} event={ev} />)}
              </div>
            </div>
          )}
        </>
      )}
      {combinedTotal === 0 && (
        <div className="text-center py-20 text-muted">
          {eventsMode ? 'No events match those filters yet. Try clearing some.' : 'Nothing matches those filters yet. Try clearing some.'}
        </div>
      )}

      {/* WALKING & TRAILS — county-wide external resource */}
      <section className="mt-20 bg-forest text-base rounded-3xl p-8 md:p-12" data-testid="walks-callout">
        <div className="max-w-3xl">
          <div className="label text-mustard mb-3">Walking &amp; trails</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-5">Lace up your boots — Leicestershire on foot</h2>
          <p className="text-base/85 leading-relaxed mb-4" data-testid="walks-overview">
            Some of the best ways to experience Leicestershire cost nothing at all. The county is criss-crossed
            with wonderful walking routes — canal towpaths, country-park circuits, village-to-village rambles and
            trails through the landscapes of the National Forest and historic Bosworth. For detailed route maps,
            directions, distances and dozens of tried-and-tested walks right across the county, we recommend the
            Leicestershire collection at GPS Routes.
          </p>
          <a href="https://www.gps-routes.co.uk/routes/home.nsf/county-walks/Leicestershire" target="_blank" rel="noopener noreferrer"
            data-testid="walks-external-link" data-outbound="website"
            className="inline-flex items-center gap-2 bg-mustard text-charcoal font-semibold px-6 py-3 rounded-full hover:bg-base transition-colors">
            Browse Leicestershire walks on GPS Routes →
          </a>
          <p className="text-xs text-base/60 mt-5" data-testid="walks-credit">
            Walking routes and maps are published by <strong>gps-routes.co.uk</strong> — our thanks to the site and
            its owner for making this collection freely available to walkers.
          </p>
          <p className="text-xs text-base/50 mt-2" data-testid="walks-disclaimer">
            Please note: this link opens an independent third-party website in a new tab. goLeicestershire has no
            control over, and accepts no liability for, the content, accuracy or availability of external sites, or
            for any loss or injury arising from use of the routes they describe. Always check conditions and walk
            within your capabilities.
          </p>
        </div>
      </section>
    </div>
  );
}
